import React, { useMemo, useState } from 'react';

const imageExtensions = new Set(['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'svg']);

const getFileExtension = (fileName = '') => {
    const normalized = String(fileName || '').trim().toLowerCase();
    const dotIndex = normalized.lastIndexOf('.');
    if (dotIndex === -1 || dotIndex === normalized.length - 1) return '';
    return normalized.slice(dotIndex + 1);
};

const getPreviewKind = (fileName = '') => {
    const extension = getFileExtension(fileName);
    if (imageExtensions.has(extension)) return 'image';
    if (extension === 'pdf') return 'pdf';
    return 'other';
};

const triggerNativeDownload = (url) => {
    if (!url) return;
    const link = document.createElement('a');
    link.href = url;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

const FileDropdownPreviewMenu = ({
    files = [],
    onDownloadAll = null,
    downloadAllLabel = 'Download all (.zip)',
    emptyLabel = 'No files',
}) => {
    const [hoveredKey, setHoveredKey] = useState('');
    const [hoverTop, setHoverTop] = useState(8);

    const normalizedFiles = useMemo(() => (
        files.map((file, index) => ({
            ...file,
            id: String(file?.id || `${file?.name || 'file'}-${index}`),
            name: String(file?.name || `File ${index + 1}`),
            previewKind: getPreviewKind(file?.name || ''),
        }))
    ), [files]);

    const hoveredFile = useMemo(
        () => normalizedFiles.find((file) => file.id === hoveredKey) || null,
        [hoveredKey, normalizedFiles]
    );

    const handleHover = (file, event) => {
        const rowRect = event.currentTarget.getBoundingClientRect();
        const menuRect = event.currentTarget.closest('.file-preview-dropdown-menu')?.getBoundingClientRect();
        const top = menuRect ? Math.max(8, rowRect.top - menuRect.top) : 8;
        setHoverTop(top);
        setHoveredKey(file.id);
    };

    const handleDownload = (event, file) => {
        event.preventDefault();
        event.stopPropagation();
        if (typeof file?.onDownload === 'function') {
            file.onDownload(file);
            return;
        }
        triggerNativeDownload(file?.downloadUrl);
    };

    const clearHover = () => setHoveredKey('');

    return (
        <ul className="dropdown-menu file-preview-dropdown-menu" onMouseLeave={clearHover}>
            {typeof onDownloadAll === 'function' && (
                <li>
                    <button
                        type="button"
                        className="dropdown-item"
                        onClick={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                            onDownloadAll();
                        }}
                    >
                        {downloadAllLabel}
                    </button>
                </li>
            )}

            {normalizedFiles.length ? normalizedFiles.map((file) => (
                <li key={file.id} onMouseEnter={(event) => handleHover(file, event)}>
                    <div className="file-preview-row">
                        <span className="file-preview-name" title={file.name}>{file.name}</span>
                        <span className="file-preview-actions">
                            {file.previewUrl ? (
                                <a
                                    href={file.previewUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="file-preview-action-link"
                                    title="Open preview"
                                    onClick={(event) => event.stopPropagation()}
                                >
                                    <i className="bx bx-show" />
                                </a>
                            ) : (
                                <span className="file-preview-action-link disabled" title="Preview unavailable">
                                    <i className="bx bx-show-alt" />
                                </span>
                            )}
                            <button
                                type="button"
                                className="file-preview-action-link file-preview-action-btn"
                                title="Download file"
                                onClick={(event) => handleDownload(event, file)}
                            >
                                <i className="bx bx-download" />
                            </button>
                        </span>
                    </div>
                </li>
            )) : (
                <li>
                    <span className="dropdown-item text-muted">{emptyLabel}</span>
                </li>
            )}

            {hoveredFile && hoveredFile.previewUrl && (
                <li className="file-preview-popover" style={{ top: hoverTop }}>
                    <div className="file-preview-popover-title" title={hoveredFile.name}>
                        {hoveredFile.name}
                    </div>
                    <div className="file-preview-popover-body">
                        {hoveredFile.previewKind === 'image' && (
                            <img
                                src={hoveredFile.previewUrl}
                                alt={hoveredFile.name}
                                className="file-preview-popover-image"
                            />
                        )}
                        {hoveredFile.previewKind === 'pdf' && (
                            <iframe
                                src={hoveredFile.previewUrl}
                                title={hoveredFile.name}
                                className="file-preview-popover-frame"
                            />
                        )}
                        {hoveredFile.previewKind === 'other' && (
                            <div className="file-preview-popover-empty">
                                <i className="bx bx-file" />
                                <span>Quick preview unavailable. Use the eye icon to open.</span>
                            </div>
                        )}
                    </div>
                </li>
            )}
        </ul>
    );
};

export default FileDropdownPreviewMenu;
